import { useState } from 'react';
import { toastManager } from '@/notifications/toasts';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useAppForm } from '@/components/app-form';
import { getFormFieldErrors, submitForm } from '@/lib/form-validation';
import { validateEmail } from './form-validation';
import { AuthCard } from './auth-card';
import { requestPasswordReset, signInWithPassword } from './api';
import * as m from '@/paraglide/messages.js';

/**
 * The external path, on the same screen as the Google button. It is folded away behind a link
 * because almost everyone here signs in with Workspace; showing two equal forms would suggest
 * a choice most people do not have.
 */
export function PasswordSignInForm() {
  const signIn = useMutation({
    retry: false,
    networkMode: 'always',
    gcTime: 0,
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signInWithPassword(email.trim(), password),
    onSuccess: () => window.location.assign('/'),
  });
  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    onSubmit: async ({ value, formApi }) => {
      try {
        await signIn.mutateAsync(value);
      } catch (cause) {
        const fields = getFormFieldErrors(cause);
        formApi.setErrorMap({ onSubmit: { fields } });
        if (Object.keys(fields).length > 0) return;
        toastManager.add({
          title: cause instanceof Error ? cause.message : m.auth_sign_in_failed(),
          type: 'error',
        });
      }
    },
  });

  return (
    <form noValidate onSubmit={(event) => void submitForm(event, form)} className="mt-5 space-y-4">
      <form.AppField
        name="email"
        validators={{
          onChange: ({ value }) => validateEmail(value),
        }}
      >
        {(field) => (
          <field.TextField
            id="sign-in-email"
            label={m.auth_email()}
            type="email"
            autoComplete="username"
            required
          />
        )}
      </form.AppField>
      <form.AppField
        name="password"
        validators={{
          onChange: ({ value }) => (!value ? m.validation_field() : undefined),
        }}
      >
        {(field) => (
          <field.TextField
            id="sign-in-password"
            label={m.auth_password()}
            type="password"
            autoComplete="current-password"
            required
          />
        )}
      </form.AppField>
      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(busy) => (
          <Button type="submit" treatment="wrap" className="w-full" disabled={busy}>
            {busy ? m.auth_signing_in() : m.auth_sign_in()}
          </Button>
        )}
      </form.Subscribe>
      <a
        href="/forgot-password"
        className="block text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        {m.auth_forgot_password()}
      </a>
    </form>
  );
}

/** Accepted reset requests keep the same response for registered and unknown addresses. */
export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const form = useAppForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      try {
        await requestPasswordReset(value.email.trim());
      } catch {
        toastManager.add({ title: m.auth_reset_request_failed(), type: 'error' });
        return;
      }
      setSent(true);
      toastManager.add({ title: m.auth_forgot_sent_title(), type: 'success' });
    },
  });

  if (sent) {
    return (
      <AuthCard title={m.auth_forgot_sent_title()} description={m.auth_forgot_sent_description()}>
        <a
          href="/"
          className="mt-6 inline-block text-sm text-primary underline-offset-4 hover:underline"
        >
          {m.auth_go_to_sign_in()}
        </a>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={m.auth_forgot_title()} description={m.auth_forgot_description()}>
      <form
        noValidate
        onSubmit={(event) => void submitForm(event, form)}
        className="mt-6 space-y-5"
      >
        <form.AppField
          name="email"
          validators={{
            onChange: ({ value }) => validateEmail(value),
          }}
        >
          {(field) => (
            <field.TextField
              id="forgot-email"
              label={m.auth_email()}
              type="email"
              autoComplete="username"
              autoFocus
              required
            />
          )}
        </form.AppField>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(busy) => (
            <Button type="submit" size="xl" treatment="wrap" className="w-full" disabled={busy}>
              {busy ? m.auth_sending() : m.auth_forgot_submit()}
            </Button>
          )}
        </form.Subscribe>
        <a
          href="/"
          className="block text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {m.auth_go_to_sign_in()}
        </a>
      </form>
    </AuthCard>
  );
}
